/**
 * SelectionScreen.tsx
 * Phase 2 — Écran "Ma Sélection" pour les vagues de retours.
 *
 * Affiche uniquement les contrats sélectionnés par l'agent
 * pour permettre un enchaînement rapide des inspections retour.
 *
 * @version 1.0.0
 */

import React from 'react'
import { useAppStore } from '../store/useAppStore'
import { offlinePhotoStore } from '../store/offlinePhotoStore'
import { Car, ArrowLeft, Trash2, CheckCircle2, Loader2, Play } from 'lucide-react'
import Button from '../components/ui/Button'
import Card   from '../components/ui/Card'
import toast  from 'react-hot-toast'

// ── Types ───────────────────────────────────────────────────────────────────
interface SelectionContract {
  id:           string | number
  rawId?:       string | number
  plate:        string
  vehicleName?: string
  clientName:   string
  clientEmail?: string
  agencyEmail?: string
  typeDocument?: string
  _selectedAt:  string
  _cacheReady:  boolean
  [key: string]: unknown
}

// ── Composant principal ─────────────────────────────────────────────────────
export default function SelectionScreen() {
  const selectionQueue        = useAppStore(s => s.selectionQueue) as SelectionContract[]
  const removeFromSelection   = useAppStore(s => s.removeFromSelectionQueue)
  const clearSelection        = useAppStore(s => s.clearSelectionQueue)
  const setStep               = useAppStore(s => s.setStep)
  const startInspection       = useAppStore(s => s.startInspection)

  const [preparingId, setPreparingId] = React.useState<string | null>(null)

  // ── Lancer un retour pour un contrat sélectionné ──────────────────────────
  const handleStartReturn = async (contract: SelectionContract) => {
    const contractId = String(contract.rawId ?? contract.id)
    setPreparingId(contractId)

    try {
      // Pré-charger les photos de départ depuis le cache
      const cached = await offlinePhotoStore.getDepartureCache(contractId)
      if (cached && Object.keys(cached).length > 0) {
        const { photoMemory } = await import('../store/photoMemoryStore')
        photoMemory.setDepartPhotosFromUrls(cached, contractId)
        console.log(`[SelectionScreen] Photos départ depuis cache: ${Object.keys(cached).length}`)
      }

      // Démarrer l'inspection
      startInspection(contract, 'retour')

      // Retirer de la file de sélection
      removeFromSelection(contractId)

      // Naviguer vers l'écran de choix véhicule
      setStep('choose')
    } catch (err: any) {
      console.error('[SelectionScreen] Erreur démarrage retour:', err)
      toast.error('Erreur au démarrage du retour')
    } finally {
      setPreparingId(null)
    }
  }

  // ── Retirer un contrat de la sélection ────────────────────────────────────
  const handleRemove = (contract: SelectionContract) => {
    const contractId = String(contract.rawId ?? contract.id)
    if (window.confirm(`Retirer ${contract.plate} de la sélection ?`)) {
      removeFromSelection(contractId)
      // Nettoyer le cache des photos si elles avaient été pré-chargées
      offlinePhotoStore.clearDepartureCache(contractId).catch(() => {})
      toast.success(`${contract.plate} retiré de la sélection`)
    }
  }

  // ── Écran vide ────────────────────────────────────────────────────────────
  if (selectionQueue.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <Car className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Aucun retour sélectionné
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">
            Sélectionnez des contrats depuis la liste pour préparer une vague de retours.
          </p>
          <Button onClick={() => setStep('contracts')} variant="primary">
            <ArrowLeft size={16} className="mr-2" />
            Retour à la liste
          </Button>
        </div>
      </div>
    )
  }

  // ── Liste des véhicules sélectionnés ──────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setStep('contracts')}
          className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          title="Retour à la liste"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-lg font-bold text-gray-800 dark:text-white">
          Ma Sélection ({selectionQueue.length})
        </h1>
        <button
          onClick={() => {
            if (window.confirm('Vider toute la sélection ?')) {
              clearSelection()
              setStep('contracts')
            }
          }}
          className="p-2 rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-gray-800 text-red-500"
          title="Vider la sélection"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-3 mb-4">
        <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
          📋 Choisissez le véhicule à inspecter. Après finalisation, vous reviendrez ici automatiquement.
        </p>
      </div>

      {/* Liste des véhicules */}
      <div className="space-y-3">
        {selectionQueue.map((contract) => {
          const contractId = String(contract.rawId ?? contract.id)
          const isPreparing = preparingId === contractId

          return (
            <Card key={contractId} className="p-4">
              <div className="flex items-center gap-4">
                {/* Icon véhicule */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  contract._cacheReady
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                    : 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400'
                }`}>
                  {contract._cacheReady
                    ? <CheckCircle2 size={24} />
                    : <Car size={24} />
                  }
                </div>

                {/* Infos contrat */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-sm text-gray-800 dark:text-white">
                      {contract.plate}
                    </span>
                    {contract._cacheReady && (
                      <span className="text-[10px] bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full font-medium">
                        Prêt
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {contract.clientName}
                  </p>
                  {contract.vehicleName && (
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                      {contract.vehicleName}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRemove(contract)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Retirer"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    onClick={() => handleStartReturn(contract)}
                    disabled={isPreparing}
                    className={`px-4 py-2 rounded-xl font-semibold text-sm text-white transition-all ${
                      isPreparing
                        ? 'bg-gray-400 cursor-wait'
                        : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                    }`}
                  >
                    {isPreparing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Play size={14} />
                        <span>Retour</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
